import {
  fetchPlayerSubscriptionById,
  fetchMatchingPlayers,
  type FiltersSubmissionPayload,
  fetchAvailableIntegrationInstances
} from "@/helpers/api/player-subscription/fetch";
import { updateReactiveObject } from "@/helpers/utils";
import { nextTick, reactive, watch } from "vue";
import { useHasUnsavedChangesHelpers } from "@/composables/utils/hasUnsavedChangesHelpers";
import { parseJsonSafely } from '@/helpers/utils';
import { orientationsMap } from "@/data/mappings/Orientations.map";
import { useRouter } from "vue-router";

const data = reactive({
  id: null as number|null,
  name: "",
  description: "",
  filters: {
    organizations: [],
    locations: [],
    orientations: [],
    playerTags: []
  },
  bindings: [] as any[],

  /**
   * Used only for a subscription that is dedicated to a single binding for a side-feature (e.g. Live Stream, Live POS Menu)
   * By side-feature —in the context of player subscriptions— we mean a feature that is implemented using player subscriptions, but is not going to be managed or listed via the
   * player subscription editor or player subscriptions list, but separately on its own editor and list (e.g. Live Stream editor and List, Live POS Menu and List).
  */
  sideFeatureBindingOptions: {} as any,
  /**
   * Similar to sideFeatureBindingOptions, used only for a side-feature subscription.
   * Check sideFeatureBindingOptions description for more details
   */
  sideFeatureIntegrationInstanceId: undefined as number|undefined,
  isActive: true,
});

const sideState = reactive({
  /** Integration instances available for setting subscription bindings */
  integrationInstances: [] as any[],

  /** Side-Feature Integration Instances (e.g. Live Streams, Live Menus) */
  sideFeatureIntegrationInstances: [] as any[],

  isLoadingIntegrationInstances: false,

  /** Whether the subscription is a side-feature subscription (e.g. Live Stream, Live POS Menu) */
  isSideFeatureSubscription: false,

  sideFeatureImplementationKey: undefined as string|undefined,
})

/** Map of side-features with key as the implementation key and value as the config of the side-feature */
const sideFeatures = {
  'liveStream': {
    baseRoute: '/live-streams'
  }
} as Record<string, any>;

const sideFeatureImplementationKeys = Object.keys(sideFeatures);

interface UsePlayerSubscriptionOptions {
  id?: number|null,
  /**
   * Similar to sideFeatureBindingOptions (in data), used only for a side-feature subscription.
   * Check sideFeatureBindingOptions description for more details
   */
  sideFeatureImplementationKey?: string,
}

export function usePlayerSubscription({
  id, 
  sideFeatureImplementationKey
 }: UsePlayerSubscriptionOptions = {}) {
  const {
    unsetHasUnsavedChanges,
    initializeHasUnsavedChangesWatcher,
    isLoading,
    hasUnsavedChanges,
    isDataInitialized
  } = useHasUnsavedChangesHelpers({
    statesToWatchForChanges: data
  });

  const router = useRouter();

  if (id !== null && id !== undefined) {
    data.id = id;
  }

  // -------- Load/Reload Helpers --------
  async function reloadPlayerSubscription() {
    await loadPlayerSubscription(data.id);
  }
  
  async function loadPlayerSubscription(
    id: number|null,
  ) {
    data.id = id;
    isLoading.value = true;
    isDataInitialized.value = false;

    await ensureUpdateIntegrationInstances();
    
    const playerSubscriptionPrepared = await getPlayerSubscriptionPrepared(id);
    updateReactiveObject(data, playerSubscriptionPrepared);

    await nextTick();
    isDataInitialized.value = true;
    isLoading.value = false;
  }

  async function getPlayerSubscriptionPrepared(subscriptionId: number|null) {
    const playerSubscriptionResponse = await fetchPlayerSubscriptionById(subscriptionId as number); 
    return transformFetchedPlayerSubscription(playerSubscriptionResponse);
  }

  function transformFetchedPlayerSubscription(playerSubscriptionResponse: any) {
    if(!playerSubscriptionResponse) console.error(`Error while transforming fetched player subscription!`);
    
    const {
      id,
      name,
      description,
      organizations,
      locations,
      orientations,
      playerTags,
      bindings: bindingsRaw,
      matchedPlayers,
      isActive
    } = playerSubscriptionResponse;

    // Parse bindings options
    const bindings = bindingsRaw?.map((binding: any) => {
      return {
        ...binding,
        bindingOptions: parseJsonSafely(binding.bindingOptions)
      }
    });
    
    if(sideFeatureImplementationKey) {
      const binding = bindings?.[0];
      if(!binding) console.warn('side-feature player subscription does not have a binding!');
      Object.assign(data.sideFeatureBindingOptions, {
        id: binding?.id,
        ...binding?.bindingOptions,
      });
    }

    // Check if the subscription is a side-feature subscription
    if(sideFeatureImplementationKey) {
      sideState.isSideFeatureSubscription = true;
      sideState.sideFeatureImplementationKey = sideFeatureImplementationKey;
    } else {
      // Check if the subscription has side-feature binding
      const firstBindingIntegrationInstanceId = bindings?.[0]?.integrationInstanceId;
      const firstBindingInstance = getInstanceById(firstBindingIntegrationInstanceId);
      sideState.isSideFeatureSubscription =
        sideFeatureImplementationKeys.includes(firstBindingInstance?.implementationKey);
      if(sideState.isSideFeatureSubscription) {
        sideState.sideFeatureImplementationKey = firstBindingInstance?.implementationKey;
      }
    }

    const bindingsPrepared = bindings?.map(binding => {
      const instance = getInstanceById(binding.integrationInstanceId);
      const humanReadableInfo = getHumanReadableInfo(binding, instance);
      const bindingPrepared = {
        ...binding,
        humanReadableInfo
      };
      return bindingPrepared;
    })

    const organizationsPrepared = organizations.map((organization: any) => ({
      ...organization,
      link: `/organizations/edit/${organization.id}`
    }));

    const locationsPrepared = locations.map((location: any) => ({
      ...location,
      link: `/locations/edit/${location.id}`
    }));

    const orientationsPrepared = orientations.map((orientation: any) => ({
      id: orientation,
      name: orientationsMap[orientation].name,
    }))

    const playerTagsPrepared = playerTags.map((playerTag: string) => {
      const [tagName, tagValue] = playerTag.split(':');
      return {
        name: `${tagName}: ${tagValue}`,
        id: playerTag
      }
    });

    const playerSubscriptionDataCleaned = {
      id,
      name,
      description,
      filters: {
        organizations: organizationsPrepared,
        locations: locationsPrepared,
        orientations: orientationsPrepared,
        playerTags: playerTagsPrepared,
      },
      bindings: bindingsPrepared,
      matchedPlayers,
      isActive
    };

    return playerSubscriptionDataCleaned;
  }

  // -------- Available Integration Instances Helpers --------
  /** Updates list of available integration instances for setting subscription bindings */
  async function ensureUpdateIntegrationInstances() {
    if(isDataInitialized.value) return;

    sideState.isLoadingIntegrationInstances = true;
    const integrationInstances = await getIntegrationInstances();

    /**
     * Integration Implementation Keys for Side-Feature Integration Instances (e.g. Live Streams, Live Menus)
     * Live Menus to be added later (For Square POS menus)
     */
    sideState.sideFeatureIntegrationInstances = integrationInstances.filter(i =>
      sideFeatureImplementationKeys.includes(i.implementationKey));
    sideState.integrationInstances = integrationInstances.filter(i =>
      !sideFeatureImplementationKeys.includes(i.implementationKey));

    if(sideFeatureImplementationKey) {
      data.sideFeatureIntegrationInstanceId = getSideFeatureIntegrationInstance(sideFeatureImplementationKey)?.id;
    }

    sideState.isLoadingIntegrationInstances = false;
  }

  /** Retrieves a side-feature integration instance by implementation key (named here as featureName) */
  function getSideFeatureIntegrationInstance(featureName: string) {
    return sideState.sideFeatureIntegrationInstances.find(i => i.implementationKey === featureName);
  }

  /** Retrieves available integration instances for setting subscription bindings */
  async function getIntegrationInstances() {
    try {
      const response = await fetchAvailableIntegrationInstances();

      if(!response) throw(`Empty response!`);

      // prepare
      const integrationInstances = response.results
        .filter((result: any) => result.isActive)
        .map((result: any) => {
          return {
            id: result.id,
            name: result.name,
            configValues: parseJsonSafely(result.configValues),
            implementationKey: result.integrationImplementation.implementationKey,
            implementationName: result.integrationImplementation.name,
            implementationId: result.integrationImplementation.id,
          }
        });

      return integrationInstances;
    } catch(error) {
      console.error(`Error while fetching available instances`)
    }
  }

  // -------- Human Readable Helpers --------
  function getHumanReadableInfo(binding: any, instance: any) {
    const bindingOptionsSummaryLines = getBindingOptionsSummaryLines(binding);
    const humanReadableInfo = {
      instanceName: instance.name,
      implementationName: instance.implementationName,
      implementationKey: instance.implementationName,
      bindingOptionsSummaryLines,
    }
    return humanReadableInfo;
  }

  function getBindingOptionsSummaryLines(binding: any) {
    if(sideState.isSideFeatureSubscription) {
      const bindingOptionsSummaryLines = Object.entries(binding.bindingOptions)
        .map(([key, value]) => `${key}: ${value}`)
      bindingOptionsSummaryLines.push(`It's Side-Feature Binding`);
      return bindingOptionsSummaryLines;
    }
    
    if(!binding.bindingOptions?.topicDetails) return [];
    
    // Prepare Options Summary Lines
    // TODO: Think about a way to make keys / values more readable for uses. Think about a way to store / cache these values for better readability
    const bindingOptionsSummaryLines = Object.entries(binding.bindingOptions?.topicDetails)
      .map(([key, value]) => `${key}: ${value
        ? (Array.isArray(value)
            ? value.join(', ')
            : value
          )
        : 'any'}`)
    if(binding.bindingOptions?.productDisplays?.length > 0) {
      bindingOptionsSummaryLines.push(`Product Displays: ${binding.bindingOptions?.productDisplays.map((i: any) => i.name).join(', ')}`)
    }
    if(binding.bindingOptions?.isSideTrigger) {
      bindingOptionsSummaryLines.push(`It's Side Trigger Binding`);
    }

    return bindingOptionsSummaryLines;
  }
  function getInstanceById(instanceId: number) {
    const instance = 
      sideFeatureImplementationKey
        ? sideState.sideFeatureIntegrationInstances.find(i => String(i.id) === String(instanceId))
        : [
            ...sideState.integrationInstances,
            ...sideState.sideFeatureIntegrationInstances
          ].find(i => String(i.id) === String(instanceId))
    return instance;
  }

  // -------- Submission Helpers --------
  function prepareFiltersForSubmission(filters: any) {
    const filtersPrepared: FiltersSubmissionPayload  = {
      organizations: filters.organizations.map((i: any) => i.id),
      locations: filters.locations.map((i: any) => i.id),
      orientations: filters.orientations.map((i: any) => i.id),
      playerTags: filters.playerTags.map((i: any) => i.id)
    }

    return filtersPrepared;
  }

  function prepareBindingsForSubmission(bindings: any[]) {
    const bindingsPrepared = bindings.map((binding: any) => {
      const { humanReadableInfo, ...rest } = binding;
      const bindingPrepared = {
        ...rest,
        bindingOptions: JSON.stringify(binding.bindingOptions)
      }
      return bindingPrepared;
    })
    return bindingsPrepared;
  }

  async function prepareDataForSubmission(dataState: any) {
    await ensureUpdateIntegrationInstances();

    const filtersPayload = prepareFiltersForSubmission(dataState.filters);
    if(sideFeatureImplementationKey) {
      const { id, ...bindingOptions } = dataState.sideFeatureBindingOptions;
      const sideFeatureBindingData = {
        id,
        bindingOptions,
        /**
         * Note: important to get the integration instance id from the
         *  data state, not from the passed-in dataState because it the
         *  integration instance id is set in the ensureUpdateIntegrationInstances
         *  function and it's not available in the passed-in dataState.
         */
        integrationInstanceId: data.sideFeatureIntegrationInstanceId,
        isActive: dataState.isActive
      }
      dataState.bindings = [sideFeatureBindingData];
    }
    const bindingsPayload = prepareBindingsForSubmission(dataState.bindings);
    const { id, name, description, isActive } = dataState;
    const dataPayload = {
      id,
      name,
      description,
      isActive,
      ...filtersPayload,
      bindings: bindingsPayload,
    }
    return dataPayload;
  }

  function routeToSideFeatureEditPage() {
    if(!sideState.sideFeatureImplementationKey) return;
    const sideFeatureBaseRoute = sideFeatures[sideState.sideFeatureImplementationKey].baseRoute;
    const subscriptionId = data.id;
    router.push(`${sideFeatureBaseRoute}/edit/${subscriptionId}`);
  }
  
  return {
    loadPlayerSubscription,
    reloadPlayerSubscription,
    prepareDataForSubmission,
    prepareFiltersForSubmission,
    getHumanReadableInfo,
    routeToSideFeatureEditPage,
    data,
    sideState,
    isLoading,
    isDataInitialized,
    hasUnsavedChanges,
    unsetHasUnsavedChanges,
    initializeHasUnsavedChangesWatcher,
    transformFetchedPlayerSubscription,
  }
}
